import {
  type ComponentProps,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { prepareReportSpecForRender } from "@/lib/report/prepareReportSpecForRender";
import { formatDateForUrl } from "@/lib/utils/dateUtils";
import { TemplateStudioSpecRenderer } from "./TemplateStudioSpecRenderer";

/** A4 width (210mm) at 96dpi. */
const A4_PREVIEW_WIDTH_PX = 794;
/** A4 height (297mm) at 96dpi. */
export const A4_PREVIEW_HEIGHT_PX = 1123;
const A4_PAGE_PADDING_PX = 24;
const A4_PAGE_CONTENT_WIDTH_PX = A4_PREVIEW_WIDTH_PX - A4_PAGE_PADDING_PX * 2;
const A4_PAGE_CONTENT_HEIGHT_PX = A4_PREVIEW_HEIGHT_PX - A4_PAGE_PADDING_PX * 2;
const A4_SECTION_GAP_ESTIMATE_PX = 16;
/** Measured height when ReportFooter only shows page numbers (no disclaimer yet). */
const REPORT_FOOTER_PAGINATION_ESTIMATE_PX = 32;

type LayoutSpec = ComponentProps<typeof TemplateStudioSpecRenderer>["spec"];
type LayoutTheme = ComponentProps<typeof TemplateStudioSpecRenderer>["theme"];

interface LayoutSpecElement {
  type: string;
  props?: Record<string, unknown>;
  children?: string[];
  [key: string]: unknown;
}

interface LayoutSpecDocument {
  root: string;
  elements: Record<string, LayoutSpecElement>;
  state?: Record<string, unknown>;
  [key: string]: unknown;
}

const toLayoutSpecDocument = (spec: LayoutSpec): LayoutSpecDocument | null => {
  if (typeof spec !== "object" || spec === null) return null;

  const candidate = spec as unknown as Partial<LayoutSpecDocument>;
  const isDocument =
    typeof candidate.root === "string" &&
    typeof candidate.elements === "object" &&
    candidate.elements !== null;

  return isDocument ? (candidate as LayoutSpecDocument) : null;
};

const getPaginationWrapperId = (
  document: LayoutSpecDocument,
): string | null => {
  const rootElement = document.elements[document.root];
  const directChildren = rootElement?.children ?? [];
  if (directChildren.length !== 1) return null;

  const wrapperId = directChildren[0];
  if (!wrapperId) return null;

  const wrapperElement = document.elements[wrapperId];
  if (
    (wrapperElement?.children?.length ?? 0) > 1 &&
    ((wrapperElement?.type === "Stack" &&
      wrapperElement.props?.direction !== "horizontal") ||
      wrapperElement?.type === "Page")
  ) {
    return wrapperId;
  }

  return null;
};

/** @public Exported for unit tests. */
export const getTopLevelChildIds = (spec: LayoutSpec): string[] => {
  const document = toLayoutSpecDocument(spec);
  if (!document) return [];

  const rootElement = document.elements[document.root];
  const directChildren = rootElement?.children ?? [];
  const wrapperId = getPaginationWrapperId(document);
  if (wrapperId)
    return document.elements[wrapperId]?.children ?? directChildren;

  return directChildren;
};

/** @public Exported for unit tests. */
export const getReportFooterChildIds = (
  spec: LayoutSpec,
  childIds: string[],
): string[] => {
  const document = toLayoutSpecDocument(spec);
  if (!document) return [];

  return childIds.filter(
    (childId) => document.elements[childId]?.type === "ReportFooter",
  );
};

/** @public Exported for unit tests. */
export const getPageableChildIds = (
  childIds: string[],
  footerChildIds: string[],
): string[] => {
  const footerIds = new Set(footerChildIds);
  return childIds.filter((childId) => !footerIds.has(childId));
};

const buildChildSpec = (spec: LayoutSpec, childId: string): LayoutSpec => {
  const document = toLayoutSpecDocument(spec);
  if (!document) return spec;

  return { ...spec, root: childId } as LayoutSpec;
};

/** @public Exported for unit tests. */
export const withReportFooterPageContext = (
  spec: LayoutSpec,
  pageIndex: number,
  totalPages: number,
): LayoutSpec => {
  if (totalPages <= 1) return spec;

  const document = toLayoutSpecDocument(spec);
  if (!document) return spec;

  let changed = false;
  const elements = { ...document.elements };
  for (const [elementId, element] of Object.entries(elements)) {
    if (element.type !== "ReportFooter") continue;
    changed = true;
    elements[elementId] = {
      ...element,
      props: {
        ...(element.props ?? {}),
        pageIndex,
        totalPages,
      },
    };
  }

  if (!changed) return spec;
  return { ...spec, elements } as LayoutSpec;
};

const resolveMeasuredChildHeight = (
  spec: LayoutSpec,
  childId: string,
  measuredHeight: number,
): number => {
  if (measuredHeight > 0) return measuredHeight;

  const document = toLayoutSpecDocument(spec);
  const element = document?.elements[childId];
  if (element?.type === "ReportFooter") {
    return REPORT_FOOTER_PAGINATION_ESTIMATE_PX;
  }

  return measuredHeight;
};

/** @public Exported for unit tests. */
export const getPageContentHeight = (
  footerChildIds: string[],
  pageableChildIds: string[],
  childHeights: Map<string, number>,
): number => {
  const footerHeight = footerChildIds.reduce(
    (height, childId) => height + (childHeights.get(childId) ?? 0),
    0,
  );
  const footerGaps = Math.max(0, footerChildIds.length - 1);
  const footerReservedHeight =
    footerHeight +
    footerGaps * A4_SECTION_GAP_ESTIMATE_PX +
    (footerChildIds.length > 0 && pageableChildIds.length > 0
      ? A4_SECTION_GAP_ESTIMATE_PX
      : 0);
  return A4_PAGE_CONTENT_HEIGHT_PX - footerReservedHeight;
};

/** @public Exported for unit tests. */
export const buildPageSpec = (
  spec: LayoutSpec,
  childIds: string[],
  pageIndex: number,
): LayoutSpec => {
  const document = toLayoutSpecDocument(spec);
  if (!document) return spec;

  const rootElement = document.elements[document.root];
  if (!rootElement) return spec;

  const pageRootId = `${document.root}__templateStudioPage${pageIndex}`;
  const wrapperId = getPaginationWrapperId(document);
  if (wrapperId) {
    const wrapperElement = document.elements[wrapperId];
    if (wrapperElement) {
      const pageWrapperId = `${wrapperId}__templateStudioPage${pageIndex}`;
      return {
        ...spec,
        root: pageRootId,
        elements: {
          ...document.elements,
          [pageRootId]: { ...rootElement, children: [pageWrapperId] },
          [pageWrapperId]: { ...wrapperElement, children: childIds },
        },
      } as LayoutSpec;
    }
  }

  return {
    ...spec,
    root: pageRootId,
    elements: {
      ...document.elements,
      [pageRootId]: { ...rootElement, children: childIds },
    },
  } as LayoutSpec;
};

export interface A4PreviewSpecPage {
  kind: "spec";
  childIds: string[];
}

export interface A4PreviewContinuationPage {
  kind: "continuation";
  childId: string;
  sliceIndex: number;
  totalSlices: number;
  sliceHeight: number;
}

export type A4PreviewPage = A4PreviewSpecPage | A4PreviewContinuationPage;

/** @public Exported for unit tests. */
export const buildPreviewPages = (
  childIds: string[],
  childHeights: Map<string, number>,
  pageContentHeight = A4_PAGE_CONTENT_HEIGHT_PX,
  firstPagePinnedChildCount = 0,
): A4PreviewPage[] => {
  const availableHeight = Math.max(1, pageContentHeight);
  const pages: A4PreviewPage[] = [];
  let currentChildIds: string[] = [];
  let currentHeight = 0;

  const flushSpecPage = () => {
    if (currentChildIds.length === 0) return;
    pages.push({ kind: "spec", childIds: [...currentChildIds] });
    currentChildIds = [];
    currentHeight = 0;
  };

  for (const childId of childIds) {
    const childHeight = Math.ceil(childHeights.get(childId) ?? 0);
    const gapHeight =
      currentChildIds.length === 0 ? 0 : A4_SECTION_GAP_ESTIMATE_PX;
    const belongsOnFirstPage =
      pages.length === 0 && currentChildIds.length < firstPagePinnedChildCount;

    if (belongsOnFirstPage) {
      currentChildIds.push(childId);
      currentHeight += gapHeight + childHeight;
      continue;
    }

    if (childHeight > availableHeight) {
      flushSpecPage();
      const totalSlices = Math.max(1, Math.ceil(childHeight / availableHeight));
      for (let sliceIndex = 0; sliceIndex < totalSlices; sliceIndex += 1) {
        pages.push({
          kind: "continuation",
          childId,
          sliceIndex,
          totalSlices,
          sliceHeight: availableHeight,
        });
      }
      continue;
    }

    const nextHeight = currentHeight + gapHeight + childHeight;

    if (currentChildIds.length > 0 && nextHeight > availableHeight) {
      flushSpecPage();
      currentChildIds = [childId];
      currentHeight = childHeight;
      continue;
    }

    currentChildIds.push(childId);
    currentHeight = nextHeight;
  }

  flushSpecPage();
  return pages;
};

const getPreviewPagesKey = (pages: A4PreviewPage[]): string =>
  pages
    .map((page) =>
      page.kind === "spec"
        ? `s:${page.childIds.join("\u0000")}`
        : `c:${page.childId}:${page.sliceIndex}/${page.totalSlices}:${page.sliceHeight}`,
    )
    .join("|");

const getPreviewPageKey = (page: A4PreviewPage, pageIndex: number): string => {
  if (page.kind === "spec") {
    return page.childIds.join("\u0000") || `spec-page-${pageIndex}`;
  }
  return `continuation-${page.childId}-${page.sliceIndex}`;
};

/** Splits a layout spec across fixed-height A4 pages for preview. */
export const PaginatedA4Preview = ({
  spec,
  theme,
}: {
  spec: LayoutSpec;
  theme?: LayoutTheme;
}) => {
  const [todayValue] = useState(() => formatDateForUrl(new Date()));
  const preparedSpec = useMemo(
    () => prepareReportSpecForRender(spec, todayValue, "authored"),
    [spec, todayValue],
  );
  const measurementRef = useRef<HTMLDivElement | null>(null);
  const topLevelChildIds = useMemo(
    () => getTopLevelChildIds(preparedSpec),
    [preparedSpec],
  );
  const footerChildIds = useMemo(
    () => getReportFooterChildIds(preparedSpec, topLevelChildIds),
    [preparedSpec, topLevelChildIds],
  );
  const pageableChildIds = useMemo(
    () => getPageableChildIds(topLevelChildIds, footerChildIds),
    [footerChildIds, topLevelChildIds],
  );
  const firstPagePinnedChildCount = useMemo(() => {
    const document = toLayoutSpecDocument(preparedSpec);
    const firstBodyIndex = pageableChildIds.findIndex((childId) => {
      const type = document?.elements[childId]?.type;
      return type !== "ReportHeader" && type !== "ReportTableOfContents";
    });
    return firstBodyIndex < 0 ? pageableChildIds.length : firstBodyIndex;
  }, [pageableChildIds, preparedSpec]);
  const [previewPages, setPreviewPages] = useState<A4PreviewPage[] | null>(
    null,
  );

  const childSpecsById = useMemo(() => {
    const next = new Map<string, LayoutSpec>();
    for (const childId of topLevelChildIds) {
      next.set(childId, buildChildSpec(preparedSpec, childId));
    }
    return next;
  }, [preparedSpec, topLevelChildIds]);

  useLayoutEffect(() => {
    setPreviewPages(null);
    if (topLevelChildIds.length === 0) return;

    const measure = () => {
      const measurementElement = measurementRef.current;
      if (!measurementElement) return;

      const nextChildHeights = new Map<string, number>();
      for (const childElement of measurementElement.querySelectorAll(
        "[data-template-studio-measure-child]",
      )) {
        const childId = childElement.getAttribute(
          "data-template-studio-measure-child",
        );
        if (!childId) continue;
        nextChildHeights.set(
          childId,
          resolveMeasuredChildHeight(
            preparedSpec,
            childId,
            childElement.getBoundingClientRect().height,
          ),
        );
      }

      setPreviewPages((previousPages) => {
        const nextPages = buildPreviewPages(
          pageableChildIds,
          nextChildHeights,
          getPageContentHeight(
            footerChildIds,
            pageableChildIds,
            nextChildHeights,
          ),
          firstPagePinnedChildCount,
        );
        if (nextPages.length === 0 && footerChildIds.length > 0) {
          nextPages.push({ kind: "spec", childIds: [] });
        }
        if (
          previousPages &&
          getPreviewPagesKey(previousPages) === getPreviewPagesKey(nextPages)
        ) {
          return previousPages;
        }
        return nextPages;
      });
    };

    const animationFrameId = window.requestAnimationFrame(measure);
    const resizeObserver = new ResizeObserver(measure);
    const measurementElement = measurementRef.current;
    if (measurementElement) {
      for (const childElement of measurementElement.querySelectorAll(
        "[data-template-studio-measure-child]",
      )) {
        resizeObserver.observe(childElement);
      }
    }

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      resizeObserver.disconnect();
    };
  }, [
    firstPagePinnedChildCount,
    footerChildIds,
    pageableChildIds,
    preparedSpec,
    topLevelChildIds,
  ]);

  const pages = useMemo((): A4PreviewPage[] => {
    return previewPages ?? [{ kind: "spec", childIds: pageableChildIds }];
  }, [pageableChildIds, previewPages]);

  type PageRenderSpec =
    | {
        kind: "continuation";
        page: A4PreviewContinuationPage;
        childSpec: LayoutSpec;
      }
    | { kind: "spec"; page: A4PreviewSpecPage; pageSpec: LayoutSpec };

  const pageRenderSpecs = useMemo((): PageRenderSpec[] => {
    let specPageIndex = 0;
    return pages.map((page) => {
      if (page.kind === "continuation") {
        return {
          kind: "continuation",
          page,
          childSpec: childSpecsById.get(page.childId) ?? preparedSpec,
        };
      }

      const entry: PageRenderSpec = {
        kind: "spec",
        page,
        pageSpec: buildPageSpec(preparedSpec, page.childIds, specPageIndex),
      };
      specPageIndex += 1;
      return entry;
    });
  }, [childSpecsById, pages, preparedSpec]);

  if (topLevelChildIds.length === 0) {
    return (
      <div className="flex w-full flex-col items-center gap-6 pb-12">
        <div className="flex flex-col items-center gap-2">
          <div
            className="bg-white shadow-sm"
            style={{
              height: A4_PREVIEW_HEIGHT_PX,
              width: A4_PREVIEW_WIDTH_PX,
            }}
          >
            <div className="h-full overflow-hidden p-6">
              <TemplateStudioSpecRenderer spec={preparedSpec} theme={theme} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col items-center gap-6 pb-12">
      <div
        ref={measurementRef}
        aria-hidden="true"
        className="pointer-events-none fixed top-0 left-[-10000px] opacity-0"
        style={{ width: A4_PAGE_CONTENT_WIDTH_PX }}
      >
        {topLevelChildIds.map((childId) => (
          <div key={childId} data-template-studio-measure-child={childId}>
            <TemplateStudioSpecRenderer
              spec={childSpecsById.get(childId) ?? preparedSpec}
              theme={theme}
            />
          </div>
        ))}
      </div>
      {pageRenderSpecs.map((renderSpec, pageIndex) => {
        if (renderSpec.kind === "continuation") {
          const footerSpec = buildPageSpec(
            preparedSpec,
            footerChildIds,
            pageIndex,
          );
          return (
            <div
              key={getPreviewPageKey(renderSpec.page, pageIndex)}
              className="flex flex-col items-center gap-2"
            >
              <div
                className="bg-white shadow-sm"
                style={{
                  height: A4_PREVIEW_HEIGHT_PX,
                  width: A4_PREVIEW_WIDTH_PX,
                }}
              >
                <div className="flex h-full flex-col overflow-hidden p-6">
                  <div
                    className="overflow-hidden"
                    style={{ height: renderSpec.page.sliceHeight }}
                    title="Continuation preview may split content mid-element in v1."
                  >
                    <div
                      style={{
                        transform: `translateY(-${renderSpec.page.sliceIndex * renderSpec.page.sliceHeight}px)`,
                      }}
                    >
                      <TemplateStudioSpecRenderer
                        spec={renderSpec.childSpec}
                        theme={theme}
                      />
                    </div>
                  </div>
                  {footerChildIds.length > 0 ? (
                    <div className="mt-auto shrink-0 pt-4">
                      <TemplateStudioSpecRenderer
                        spec={withReportFooterPageContext(
                          footerSpec,
                          pageIndex,
                          pages.length,
                        )}
                        theme={theme}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
              {pages.length > 1 ? (
                <div className="text-xs text-gray-400">
                  Page {pageIndex + 1}
                  {renderSpec.page.totalSlices > 1
                    ? ` (${renderSpec.page.sliceIndex + 1}/${renderSpec.page.totalSlices})`
                    : null}
                </div>
              ) : null}
            </div>
          );
        }

        const footerSpec = buildPageSpec(
          preparedSpec,
          footerChildIds,
          pageIndex,
        );
        return (
          <div
            key={getPreviewPageKey(renderSpec.page, pageIndex)}
            className="flex flex-col items-center gap-2"
          >
            <div
              className="bg-white shadow-sm"
              style={{
                height: A4_PREVIEW_HEIGHT_PX,
                width: A4_PREVIEW_WIDTH_PX,
              }}
            >
              <div className="flex h-full flex-col overflow-y-auto overflow-x-hidden p-6">
                {renderSpec.page.childIds.length > 0 ? (
                  <div className="shrink-0">
                    <TemplateStudioSpecRenderer
                      spec={renderSpec.pageSpec}
                      theme={theme}
                    />
                  </div>
                ) : null}
                {footerChildIds.length > 0 ? (
                  <div className="mt-auto shrink-0 pt-4">
                    <TemplateStudioSpecRenderer
                      spec={withReportFooterPageContext(
                        footerSpec,
                        pageIndex,
                        pages.length,
                      )}
                      theme={theme}
                    />
                  </div>
                ) : null}
              </div>
            </div>
            {pages.length > 1 ? (
              <div className="text-xs text-gray-400">Page {pageIndex + 1}</div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};
